import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ShoppingBag } from 'lucide-react';
import { Button } from '../ui/button';
import { SHOP_URL } from '../../lib/constants';

const LOGO_URL = "/nates-logo.svg";

// Instagram colored icon component
const InstagramColored = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#FFDC80" />
        <stop offset="25%" stopColor="#FCAF45" />
        <stop offset="50%" stopColor="#F77737" />
        <stop offset="75%" stopColor="#F56040" />
        <stop offset="100%" stopColor="#C13584" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#instagram-gradient)" strokeWidth="2" fill="none" />
    <circle cx="12" cy="12" r="4" stroke="url(#instagram-gradient)" strokeWidth="2" fill="none" />
    <circle cx="17.5" cy="6.5" r="1.5" fill="url(#instagram-gradient)" />
  </svg>
);

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Our Story', path: '/our-story' },
    { name: 'Services', path: '/services' },
    { name: 'Transfer Rx', path: '/transfer' },
    { name: 'Refill', path: '/refill' },
    { name: 'Delivery', path: '/delivery' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-9 left-0 right-0 z-50 glass border-b border-slate-200/50">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Nate's Pharmacy"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium ${isActive(link.path) ? 'text-primary' : 'text-slate-600'
                  }`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://www.instagram.com/nates.pharmacy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform"
              aria-label="Instagram"
              data-testid="instagram-link"
            >
              <InstagramColored className="w-6 h-6" />
            </a>
            <a
              href={SHOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-slate-900 text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-slate-800 transition-colors flex items-center gap-2"
              data-testid="shop-online-button"
            >
              <ShoppingBag className="w-4 h-4" />
              Shop Online
            </a>
            <a
              href="tel:+17187970200"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-5 py-2.5 rounded-full font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/20 flex items-center gap-2"
              data-testid="call-button"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-white lg:hidden" data-testid="mobile-menu">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between px-4 h-16 border-b border-slate-100 bg-white">
            <img src={LOGO_URL} alt="Nate's Pharmacy" className="h-10 w-auto" />
            <button
              className="p-2 text-slate-700 hover:bg-slate-100 rounded-full transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="px-4 py-6 overflow-y-auto bg-white" style={{ height: 'calc(100vh - 64px)' }}>
            <nav className="flex flex-col gap-2 mb-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg font-medium py-4 px-5 rounded-2xl transition-all ${isActive(link.path)
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20'
                      : 'text-slate-700 bg-slate-50 hover:bg-slate-100'
                    }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="space-y-3">
              <a
                href={SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-slate-900 text-white py-4 rounded-2xl font-medium hover:bg-slate-800 transition-colors"
                data-testid="mobile-shop-online-button"
              >
                <ShoppingBag className="w-5 h-5" />
                Shop Online
              </a>
              <a
                href="tel:+17187970200"
                className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-2xl font-medium hover:opacity-90 transition-opacity"
                data-testid="mobile-call-button"
              >
                <Phone className="w-5 h-5" />
                Call (718) 797-0200
              </a>
              <a
                href="https://www.instagram.com/nates.pharmacy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white py-4 rounded-2xl font-medium hover:opacity-90 transition-opacity"
                data-testid="mobile-instagram-button"
              >
                <InstagramColored className="w-5 h-5" />
                Follow @nates.pharmacy
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
